import { Box, LinearProgress, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import type { DownloadQueueItem } from 'store/LoadingStore';

type DownloadControllerProps = {
    queue: DownloadQueueItem[];
    discovering?: boolean;
    progressMode?: 'files' | 'bytes';
    showPrimarySummary?: boolean;
    secondaryAlign?: 'space-between' | 'center';
    compact?: boolean;
    showReadyMetric?: boolean;
    showDataTotal?: boolean;
};
const RECENT_DOWNLOAD_WINDOW_MS = 3000;

export const formatBytes = (bytes: number): string => {
    if (bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[unitIndex]}`;
};

const formatTime = (seconds: number | null): string => {
    if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return '--';
    if (seconds < 60) return `${Math.ceil(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remSec = Math.ceil(seconds % 60);
    return `${minutes}m ${remSec}s`;
};

const joinSegments = (segments: string[]) => segments.filter((segment) => segment.length > 0).join(' • ');

const DownloadController: React.FC<DownloadControllerProps> = ({
    queue,
    discovering = false,
    progressMode = 'files',
    showPrimarySummary = true,
    secondaryAlign = 'space-between',
    compact = false,
    showReadyMetric = true,
    showDataTotal = false,
}) => {
    const smoothedRateRef = React.useRef<number | null>(null);
    const lastKnownEtaRef = React.useRef<number | null>(null);
    const lastEtaApproxRef = React.useRef<boolean>(true);

    const stats = useMemo(() => {
        const now = Date.now();
        const totalFiles = queue.length;
        const completedFiles = queue.filter((item) => item.status === 'completed' || item.status === 'cached').length;
        const downloadingFiles = queue.filter((item) => item.status === 'downloading').length;
        const failedFiles = queue.filter((item) => item.status === 'error').length;
        const processedFiles = completedFiles + failedFiles;
        const remainingFiles = Math.max(totalFiles - processedFiles, 0);

        const totalKnownBytes = queue.reduce((sum, item) => sum + (item.totalBytes ?? 0), 0);
        const loadedBytes = queue.reduce((sum, item) => {
            const cap = item.totalBytes ?? item.loadedBytes;
            return sum + Math.min(item.loadedBytes, cap);
        }, 0);
        const remainingKnownBytes = queue.reduce((sum, item) => {
            if (item.status === 'completed' || item.status === 'cached' || item.status === 'error') return sum;
            if (item.totalBytes === null) return sum;
            return sum + Math.max(item.totalBytes - item.loadedBytes, 0);
        }, 0);
        const totalCurrentBytes = queue
            .filter((item) => item.status === 'downloading' || item.status === 'completed')
            .sort((a, b) => b.updatedAt - a.updatedAt)[0]?.totalBytes;
        const remainingUnknownFiles = queue.filter(
            (item) => item.status !== 'completed' && item.status !== 'cached' && item.status !== 'error' && item.totalBytes === null
        ).length;
        const activeRate = queue.reduce((sum, item) => sum + (item.status === 'downloading' ? item.rateBytesPerSecond : 0), 0);
        const recentRate = queue.reduce(
            (sum, item) => (now - item.updatedAt <= 1200 && item.rateBytesPerSecond > 0 ? sum + item.rateBytesPerSecond : sum),
            0
        );
        const totalRate = activeRate > 0 ? activeRate : recentRate;

        const progressByFiles = totalFiles > 0 ? (processedFiles / totalFiles) * 100 : 0;
        const progressByBytes = totalKnownBytes > 0 ? (loadedBytes / totalKnownBytes) * 100 : 0;
        const progress = progressMode === 'bytes' && totalKnownBytes > 0 ? progressByBytes : progressByFiles;

        const firstStartAt = queue.reduce((min, item) => Math.min(min, item.startedAt), Number.POSITIVE_INFINITY);
        const hasValidStart = Number.isFinite(firstStartAt);
        const elapsedSeconds = hasValidStart ? Math.max((now - firstStartAt) / 1000, 0) : 0;
        const avgSecondsPerProcessed = processedFiles > 0 ? elapsedSeconds / processedFiles : null;
        const fileBasedEta = avgSecondsPerProcessed && remainingFiles > 0 ? avgSecondsPerProcessed * remainingFiles : null;
        const byteBasedEta = remainingKnownBytes > 0 && totalRate > 0 ? Math.max(remainingKnownBytes / totalRate, 0) : null;
        const unknownFilesPenalty =
            avgSecondsPerProcessed && remainingUnknownFiles > 0 ? avgSecondsPerProcessed * remainingUnknownFiles : null;
        const remainingSeconds = byteBasedEta !== null ? Math.max(byteBasedEta + (unknownFilesPenalty ?? 0), 0) : (fileBasedEta ?? null);
        const etaApprox = remainingUnknownFiles > 0 || byteBasedEta === null;

        return {
            totalFiles,
            completedFiles,
            downloadingFiles,
            failedFiles,
            processedFiles,
            remainingFiles,
            totalKnownBytes,
            loadedBytes,
            totalCurrentBytes: totalCurrentBytes ?? null,
            totalRate,
            remainingSeconds,
            etaApprox,
            progress: Math.min(100, Math.max(0, progress)),
        };
    }, [queue, progressMode]);

    if (stats.totalRate > 0) {
        const previous = smoothedRateRef.current ?? stats.totalRate;
        const alpha = 0.25;
        smoothedRateRef.current = previous * (1 - alpha) + stats.totalRate * alpha;
    }
    if (stats.remainingSeconds !== null && Number.isFinite(stats.remainingSeconds)) {
        lastKnownEtaRef.current = stats.remainingSeconds;
        lastEtaApproxRef.current = stats.etaApprox;
    }

    const displayRate = smoothedRateRef.current ?? 0;
    const displayEta = stats.remainingSeconds ?? lastKnownEtaRef.current;
    const displayEtaApprox = stats.remainingSeconds !== null ? stats.etaApprox : lastEtaApproxRef.current;
    const displayDownloadedBytes = stats.loadedBytes;
    const hasLiveDownload = stats.downloadingFiles > 0;
    const hasRecentDownloadActivity = queue.some(
        (item) =>
            Date.now() - item.updatedAt <= RECENT_DOWNLOAD_WINDOW_MS &&
            (item.status === 'downloading' || ((item.status === 'completed' || item.status === 'error') && (item.totalBytes ?? 0) > 0))
    );
    const shouldShowDownloadMetrics = hasLiveDownload || hasRecentDownloadActivity;
    const hasPendingFiles = stats.remainingFiles > 0;
    const displayDataMetric =
        showDataTotal && stats.totalCurrentBytes !== null
            ? `Data ${formatBytes(displayDownloadedBytes)} / ${formatBytes(stats.totalCurrentBytes)}`
            : `Data ${formatBytes(displayDownloadedBytes)}`;

    return (
        <Box
            className="loading-card-progress"
            sx={{
                ...(compact
                    ? {
                          gap: 0.5,
                      }
                    : {}),
            }}
        >
            <LinearProgress variant="determinate" value={stats.progress} className="loading-card-progress-bar" />
            <Box
                className="loading-summary"
                sx={{
                    justifyContent: secondaryAlign,
                    ...(showPrimarySummary
                        ? {}
                        : {
                              ...(compact
                                  ? {
                                        minHeight: 0,
                                        mt: 0,
                                    }
                                  : {
                                        minHeight: 24,
                                        mt: 0.25,
                                    }),
                          }),
                }}
            >
                {showPrimarySummary ? (
                    <Typography variant="body2" className="loading-summary-text">
                        {stats.totalFiles > 0
                            ? `${stats.processedFiles}/${stats.totalFiles} files processed`
                            : discovering
                              ? 'Discovering files...'
                              : 'Waiting for downloads...'}
                    </Typography>
                ) : null}
                <Typography variant="caption" className="loading-summary-meta" sx={{ color: 'var(--loading-summary-text)' }}>
                    {discovering
                        ? 'Discovering files...'
                        : !shouldShowDownloadMetrics
                          ? joinSegments([
                                hasPendingFiles ? 'Preparing next files...' : '',
                                showReadyMetric ? `${stats.completedFiles} ready` : '',
                                stats.failedFiles > 0 ? `${stats.failedFiles} failed` : '',
                            ])
                          : joinSegments([
                                displayDataMetric,
                                `Speed ${formatBytes(displayRate)}/s`,
                                `${displayEtaApprox ? 'ETA ~' : 'ETA'} ${formatTime(displayEta)}`,
                                showReadyMetric ? `${stats.completedFiles} ready` : '',
                                stats.failedFiles > 0 ? `${stats.failedFiles} failed` : '',
                            ])}
                </Typography>
            </Box>
        </Box>
    );
};

export default DownloadController;
